/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import {
	ArticleComponentsSection,
	CosmosComponentContextProps,
	getHero,
	heroOwnsHeading,
	getSections,
	SectionTypename,
	useCosmosComponentContext,
	useScrollClientHash,
} from "@cosmos/components";
import { PageBlogDetailFragment } from "@cosmos/hygraph-schema";
import { useAuth } from "@cosmos/next-hooks";

import { css } from "@panda/css";

import { TemplateProviders } from "../templates/TemplateProviders";

interface CosmosPageProps {
	pageData?: PageBlogDetailFragment;
	componentContext?: CosmosComponentContextProps;
	/**
	 * `?mode=game` / `?mode=iosgame` — an in-game or console webview, which
	 * cannot navigate away and back. Neutralises links in the article body, drops
	 * article components carrying non-allowlisted links, and hides `refSections`
	 * entirely (see docs/game-mode.md).
	 */
	isGameMode?: boolean;
}

const sectionExtraProps = (__typename: SectionTypename): any => {
	return __typename === "CosmosSectionContentLargeImage" ? { enableAnimate: false } : {};
};

export const CosmosPageBlogDetail = ({
	pageData,
	componentContext,
	isGameMode,
}: CosmosPageProps): React.JSX.Element => {
	const contextFromProvider = useCosmosComponentContext();
	const ctx = componentContext ?? contextFromProvider;
	useAuth(!ctx.enableAuth);
	useScrollClientHash();

	// Only a hero that renders an h1 owns it; a decorative hero (e.g. a glow with no title)
	// must not suppress the first section's promotion — gate on heroOwnsHeading, not presence.
	const hero = getHero(pageData?.hero, pageData?.blogConfig);

	return (
		<TemplateProviders componentContext={componentContext} isGameMode={isGameMode}>
			{hero}
			<div className={css({ paddingBlockStart: "eds.dimension.32", paddingBlockEnd: "eds.dimension.64" })}>
				{/*
				  No `nAnimatedAsset`: the article body's leading image must not animate
				  on load. `Animate` fires as soon as an element is within the initial
				  viewport, so a body image high on the page played the hero's own
				  entrance (same slideLeft, same 0.25s delay) while the body paragraph
				  above it stayed still. That reads as broken. The hero animates its own
				  title and image; body content does not borrow that motion. See
				  MW-13580.
				*/}
				<ArticleComponentsSection articleComponents={pageData?.articleComponents} isGameMode={isGameMode} />
				{/*
				  refSections are a separate component tree that never receives the
				  game-mode flag, so their links can't be neutralised. Rather than
				  ship live links into a webview with no nav, they're dropped
				  wholesale in game mode. Legal pages served in game mode are the
				  article body, so nothing load-bearing is lost.
				*/}
				{!isGameMode && pageData?.articleComponents.length && pageData?.refSections?.length ? (
					<div className={css({ paddingBlock: "eds.dimension.32" })} />
				) : (
					""
				)}
				{isGameMode ? null : getSections(pageData?.refSections, sectionExtraProps, heroOwnsHeading(pageData?.hero))}
			</div>
		</TemplateProviders>
	);
};
